import { useState, useEffect } from 'react';
import BusAgentLogo from '../img/BusAgent.png';
import BusImage from '../img/Bus.svg';
import BusGuyImage from '../img/BusGuy.svg';
import BusShieldImage from '../img/BusShield.svg';
import BusShieldImageEn from '../img/BusShieldEn.svg';
import ContactFormModal from './ContactFormModal';
import { useTranslation } from '../i18n/useTranslation';
import { trackContactClick } from '../track';

const Header = () => {
  const { locale, setLocale, t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState(null);
  const [mobileLanguageOpen, setMobileLanguageOpen] = useState(false);
  const [desktopLanguageOpen, setDesktopLanguageOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  // Helper function to check if a path is active
  const isPathActive = (path) => {
    return currentPath === path;
  };

  // Helper function to check if a category is active
  const isCategoryActive = (category) => {
    return currentPath.startsWith(`/${category}`);
  };

  // Helper function to get the active color for a path
  const getActiveColor = (path) => {
    // Produkte - all soft blue
    if (path?.startsWith('/produkte/')) {
      return 'text-blue-300';
    }

    // Anwender - specific colors
    if (path === '/anwender/verkehrsverbuende-und-aufgabentraeger') return 'text-orange-300';
    if (path === '/anwender/verkehrsunternehmen') return 'text-blue-300';
    if (path === '/anwender/fahrpersonal-binden') return 'text-orange-300';
    if (path === '/anwender/steigerung-der-fahrgastzufriedenheit') return 'text-orange-300';
    if (path === '/anwender/betriebskosten-senken') return 'text-blue-300';

    // Unternehmen - specific colors
    if (path === '/unternehmen/ueber-uns') return 'text-blue-300';
    if (path === '/unternehmen/kontakt') return 'text-blue-300';
    if (path === '/unternehmen/career') return 'text-orange-300';
    if (path === '/unternehmen/veranstaltungen') return 'text-orange-300';

    return '';
  };

  // Helper function to get the parent category color based on active child
  const getCategoryColor = (category) => {
    if (!isCategoryActive(category)) return 'text-brand-dark-blue hover:text-blue-600';

    // Check which specific item is active to determine parent color
    if (category === 'produkte') {
      return 'text-blue-300'; // All produkte items are blue
    }

    if (category === 'anwender') {
      if (isPathActive('/anwender/verkehrsverbuende-und-aufgabentraeger') ||
          isPathActive('/anwender/fahrpersonal-binden') ||
          isPathActive('/anwender/steigerung-der-fahrgastzufriedenheit')) {
        return 'text-orange-300';
      }
      return 'text-blue-300';
    }

    if (category === 'unternehmen') {
      if (isPathActive('/unternehmen/career') ||
          isPathActive('/unternehmen/veranstaltungen')) {
        return 'text-orange-300';
      }
      return 'text-blue-300';
    }

    return 'text-blue-300';
  };

  const navigationItems = {
    produkte: {
      title: t('header.produkte'),
      sections: [
        {
          items: [
            {
              title: t('header.umlaufUndDienstplanung.title'),
              description: t('header.umlaufUndDienstplanung.description'),
              iconColor: '#93c5fd',
              link: '/produkte/umlauf-und-dienstplanung'
            },
            {
              title: t('header.disposition.title'),
              description: t('header.disposition.description'),
              iconColor: '#93c5fd',
              link: '/produkte/disposition'
            },
            {
              title: t('header.fehlermeldung.title'),
              description: t('header.fehlermeldung.description'),
              iconColor: '#93c5fd',
              link: '/produkte/fehlermeldung'
            },
            {
              title: t('header.lohnabrechnung.title'),
              description: t('header.lohnabrechnung.description'),
              iconColor: '#93c5fd',
              link: '/produkte/lohnabrechnung'
            },
            {
              title: t('header.angebotserstellung.title'),
              description: t('header.angebotserstellung.description'),
              iconColor: '#93c5fd',
              link: '/produkte/angebotserstellung',
              beta: true
            }
          ]
        }
      ],
      footer: null
    },
    anwender: {
      title: t('header.anwender'),
      sections: [
        {
          heading: t('header.nachAnwendern'),
          items: [
            {
              title: t('header.verkehrsverbuende.title'),
              description: t('header.verkehrsverbuende.description'),
              link: '/anwender/verkehrsverbuende-und-aufgabentraeger'
            },
            {
              title: t('header.verkehrsunternehmen.title'),
              description: t('header.verkehrsunternehmen.description'),
              link: '/anwender/verkehrsunternehmen'
            }
          ]
        },
        {
          heading: t('header.nachAnwendungsfall'),
          items: [
            {
              title: t('header.fahrpersonalBinden.title'),
              description: t('header.fahrpersonalBinden.description'),
              link: '/anwender/fahrpersonal-binden'
            },
            {
              title: t('header.fahrgastzufriedenheit.title'),
              description: t('header.fahrgastzufriedenheit.description'),
              link: '/anwender/steigerung-der-fahrgastzufriedenheit'
            },
            {
              title: t('header.betriebskostenSenken.title'),
              description: t('header.betriebskostenSenken.description'),
              link: '/anwender/betriebskosten-senken'
            }
          ]
        }
      ]
    },
    unternehmen: {
      title: t('header.unternehmen'),
      sections: [
        {
          items: [
            { title: t('header.ueberUns'), link: '/unternehmen/ueber-uns' },
            { title: t('header.kontakt'), link: '/unternehmen/kontakt' },
            { title: t('header.career'), link: '/unternehmen/career' },
            { title: t('header.veranstaltungen'), link: '/unternehmen/veranstaltungen' }
          ]
        }
      ]
    }
  };


  return (
    <>
      <header 
        className="bg-white fixed top-0 left-0 right-0 z-50 shadow-sm"
        onMouseLeave={(e) => {
          // Only close dropdown if leaving header area entirely
          const relatedTarget = e.relatedTarget;
          if (relatedTarget && typeof relatedTarget.closest === 'function') {
            if (!relatedTarget.closest('.dropdown-menu')) {
              setActiveDropdown(null);
            }
          } else {
            // If no valid related target, close dropdown
            setActiveDropdown(null);
          }
        }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <div className="flex items-center space-x-8 lg:space-x-12">
              <a href="/" className="flex-shrink-0 flex items-center">
                <img src={BusAgentLogo} alt="BusAgent" className="h-16 lg:h-20 w-auto cursor-pointer" />
              </a>

              <nav
                className="hidden lg:flex lg:items-center lg:space-x-10 ml-auto"
              >
                {Object.entries(navigationItems).map(([key, item]) => (
                  <div
                    key={key}
                    data-key={key}
                    className="relative"
                    onMouseEnter={() => setActiveDropdown(key)}
                  >
                    <button className={`${getCategoryColor(key)} text-lg font-semibold transition cursor-pointer py-2`}>
                      {item.title}
                    </button>
                  </div>
                ))}
              </nav>
            </div>

            <div className="flex items-center space-x-4 ml-auto">
              <div className="relative">
                <button
                  onMouseEnter={() => setDesktopLanguageOpen(true)}
                  onMouseLeave={() => setDesktopLanguageOpen(false)}
                  className="hidden sm:flex items-center space-x-2 text-brand-dark-blue hover:text-blue-600 transition cursor-pointer px-3 py-2 rounded-lg hover:bg-gray-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-semibold text-base">{t('header.language')}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform ${desktopLanguageOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Language Dropdown */}
                <div
                  onMouseEnter={() => setDesktopLanguageOpen(true)}
                  onMouseLeave={() => setDesktopLanguageOpen(false)}
                  className={`absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 z-[100] ${
                    desktopLanguageOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}
                >
                  <button
                    onClick={() => {
                      setLocale('de');
                      setDesktopLanguageOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-t-lg flex items-center space-x-3"
                  >
                    {/* German Flag Icon */}
                    <svg className="w-5 h-4" viewBox="0 0 20 14" fill="none">
                      <rect width="20" height="4.67" fill="#000000"/>
                      <rect y="4.67" width="20" height="4.67" fill="#DD0000"/>
                      <rect y="9.33" width="20" height="4.67" fill="#FFCE00"/>
                    </svg>
                    <span className="text-gray-700 font-medium">{t('header.deutsch')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setLocale('en');
                      setDesktopLanguageOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-b-lg flex items-center space-x-3"
                  >
                    {/* UK Flag Icon */}
                    <svg className="w-5 h-4" viewBox="0 0 20 14" fill="none">
                      <rect width="20" height="14" fill="#012169"/>
                      <path d="M0 0L20 14M20 0L0 14" stroke="white" strokeWidth="2.8"/>
                      <path d="M0 0L20 14M20 0L0 14" stroke="#C8102E" strokeWidth="1.4"/>
                      <path d="M10 0V14M0 7H20" stroke="white" strokeWidth="4.67"/>
                      <path d="M10 0V14M0 7H20" stroke="#C8102E" strokeWidth="2.8"/>
                    </svg>
                    <span className="text-gray-700 font-medium">{t('header.english')}</span>
                  </button>
                </div>
              </div>
              <button
                onClick={() => {
                  trackContactClick('header-desktop');
                  setIsContactModalOpen(true);
                }}
                className="hidden sm:block text-base font-bold text-white bg-gradient-to-r from-blue-500 to-orange-400 rounded-full px-8 py-4 hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:from-blue-600 hover:to-orange-500 cursor-pointer"
              >
                {t('header.ctaButton')}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-brand-dark-blue hover:bg-gray-100 transition-all"
              >
                {mobileMenuOpen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Dropdown Menus */}
        {activeDropdown && (
          <div
            className="dropdown-menu absolute left-0 right-0 bg-white shadow-xl border-t border-gray-100"
            onMouseEnter={() => {
              // Keep dropdown open while hovering
              setActiveDropdown(activeDropdown);
            }}
            onMouseLeave={(e) => {
              // Only close if not going back to nav
              const relatedTarget = e.relatedTarget;
              if (relatedTarget && typeof relatedTarget.closest === 'function') {
                if (!relatedTarget.closest('nav')) {
                  setActiveDropdown(null);
                }
              } else {
                setActiveDropdown(null);
              }
            }}
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-0">
              {activeDropdown === 'produkte' && (
                <div className="grid grid-cols-12 gap-8 pb-8">
                  {/* Product Items - Left Side */}
                  <div className="col-span-8">
                    <div className="grid grid-cols-2 gap-x-16">
                      {/* First Column - Items 0-2 */}
                      <div className="space-y-10">
                        {navigationItems.produkte.sections[0].items.slice(0, 3).map((item, index) => (
                          <a
                            key={index}
                            href={item.link}
                            className="group flex space-x-4 cursor-pointer"
                          >
                            <div className="flex-shrink-0">
                              <div
                                className="w-12 h-12 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${item.iconColor}20` }}
                              >
                                <svg className="w-6 h-6" fill={item.iconColor} viewBox="0 0 24 24">
                                  <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                                </svg>
                              </div>
                            </div>
                            <div className="flex-1">
                              <h3 className={`font-bold ${isPathActive(item.link) ? getActiveColor(item.link) : 'text-brand-dark-blue group-hover:text-blue-600'} transition mb-2 flex items-center gap-2`}>
                                <span>{item.title}</span>
                                {item.beta && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-600">
                                    Beta
                                  </span>
                                )}
                              </h3>
                              <p className="text-sm text-gray-600 leading-relaxed pb-1">
                                {item.description}
                              </p>
                            </div>
                          </a>
                        ))}
                      </div>

                      {/* Second Column - Items 3-5 */}
                      <div className="space-y-10">
                        {navigationItems.produkte.sections[0].items.slice(3, 6).map((item, index) => (
                          <a
                            key={index + 3}
                            href={item.link}
                            className="group flex space-x-4 cursor-pointer"
                          >
                            <div className="flex-shrink-0">
                              <div
                                className="w-12 h-12 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${item.iconColor}20` }}
                              >
                                <svg className="w-6 h-6" fill={item.iconColor} viewBox="0 0 24 24">
                                  <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                                </svg>
                              </div>
                            </div>
                            <div className="flex-1">
                              <h3 className={`font-bold ${isPathActive(item.link) ? getActiveColor(item.link) : 'text-brand-dark-blue group-hover:text-blue-600'} transition mb-2 flex items-center gap-2`}>
                                <span>{item.title}</span>
                                {item.beta && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-600">
                                    Beta
                                  </span>
                                )}
                              </h3>
                              <p className="text-sm text-gray-600 leading-relaxed pb-1">
                                {item.description}
                              </p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Bus Image - Right Side */}
                  <div className="col-span-4 flex items-center justify-end relative">
                    <img src={BusImage} alt="Bus" className="w-auto h-auto max-w-sm" style={{ marginRight: '-100px' }} />
                  </div>
                </div>
              )}

              {activeDropdown === 'anwender' && (
                <div className="w-full relative">
                  <div className="grid grid-cols-12 gap-0">
                    {/* NACH ANWENDERN - Left Side with Blue Background */}
                    <div className="col-span-4 bg-blue-50 p-8 rounded-l-lg">
                      <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-6">
                        {navigationItems.anwender.sections[0].heading}
                      </h3>
                      <div className="space-y-6">
                        {navigationItems.anwender.sections[0].items.map((item, itemIndex) => (
                          <a
                            key={itemIndex}
                            href={item.link}
                            className="block group cursor-pointer"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                <div
                                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                                  style={{ backgroundColor: `${itemIndex === 0 ? '#fb923c' : '#3b82f6'}20` }}
                                >
                                  <svg className="w-6 h-6" fill={itemIndex === 0 ? '#fb923c' : '#3b82f6'} viewBox="0 0 24 24">
                                    {itemIndex === 0 && (
                                      // Office Building Icon for Verkehrsverbünde
                                      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                                    )}
                                    {itemIndex === 1 && (
                                      // Bus Icon for Verkehrsunternehmen
                                      <path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"/>
                                    )}
                                  </svg>
                                </div>
                              </div>
                              <div>
                                <h4 className={`font-bold ${isPathActive(item.link) ? getActiveColor(item.link) : 'text-blue-900 group-hover:text-blue-700'} transition mb-2`}>
                                  {item.title}
                                </h4>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                  {item.description}
                                </p>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* NACH ANWENDUNGSFALL - Middle with White Background */}
                    <div className="col-span-5 bg-white p-8">
                      <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-6">
                        {navigationItems.anwender.sections[1].heading}
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                        {navigationItems.anwender.sections[1].items.map((item, itemIndex) => {
                          // Define colors for each item
                          const colors = [
                            '#fb923c', // Orange for Fahrpersonal
                            '#fb923c', // Orange for Fahrgastzufriedenheit
                            '#3b82f6'  // Blue for Betriebskosten
                          ];
                          const itemColor = colors[itemIndex] || '#E91E8C';

                          return (
                            <a
                              key={itemIndex}
                              href={item.link}
                              className="block group cursor-pointer"
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 mt-1">
                                  <div
                                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: `${itemColor}20` }}
                                  >
                                    <svg className="w-6 h-6" fill={itemColor} viewBox="0 0 24 24">
                                      {itemIndex === 0 && (
                                        // Users/People icon for Fahrpersonal
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                      )}
                                      {itemIndex === 1 && (
                                        // Happy face for passenger satisfaction
                                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                                      )}
                                      {itemIndex === 2 && (
                                        // Money/Cost reduction icon
                                        <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                                      )}
                                    </svg>
                                  </div>
                                </div>
                                <div>
                                  <h4 className={`font-bold ${isPathActive(item.link) ? getActiveColor(item.link) : 'text-brand-dark-blue group-hover:text-blue-600'} transition mb-1`}>
                                    {item.title}
                                  </h4>
                                  <p className="text-sm text-gray-600 leading-relaxed">
                                    {item.description}
                                  </p>
                                </div>
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>

                    {/* Bus Guy Image - Right Side */}
                    <div className="col-span-3 flex items-end justify-end relative" style={{ alignSelf: 'flex-end' }}>
                      <img
                        src={BusGuyImage}
                        alt="Bus Driver"
                        className="w-auto object-contain"
                        style={{
                          height: '320px',
                          display: 'block'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeDropdown === 'unternehmen' && (
                <div className="w-full relative">
                  <div className="grid grid-cols-12 gap-8">
                    {/* Menu Items - Left Side */}
                    <div className="col-span-8">
                      <div className="grid grid-cols-2 gap-x-16 gap-y-10 pt-4">
                        {navigationItems.unternehmen.sections[0].items.map((item, index) => {
                          // Define colors for each item (blue and orange variations)
                          const colors = [
                            '#3b82f6', // Blue for Über uns
                            '#3b82f6', // Blue for Kontakt
                            '#fb923c', // Orange for Career
                            '#fb923c'  // Orange for Veranstaltungen
                          ];
                          const itemColor = colors[index] || '#E91E8C';

                          const icons = [
                            // Über uns - Info icon
                            <svg className="w-6 h-6" fill={itemColor} viewBox="0 0 24 24">
                              <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                            </svg>,
                            // Kontakt - Phone icon
                            <svg className="w-6 h-6" fill={itemColor} viewBox="0 0 24 24">
                              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                            </svg>,
                            // Career - Briefcase icon
                            <svg className="w-6 h-6" fill={itemColor} viewBox="0 0 24 24">
                              <path d="M10 2v2H5.03c-1.1 0-2 .9-2 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H14V2h-4zm4 2h-4V3.5c0-.28.22-.5.5-.5h3c.28 0 .5.22.5.5V4zm-2 3c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 1c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zM5 7h14v11H5V7z"/>
                            </svg>,
                            // Veranstaltungen - Calendar icon
                            <svg className="w-6 h-6" fill={itemColor} viewBox="0 0 24 24">
                              <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/>
                            </svg>
                          ];

                          return (
                            <a
                              key={index}
                              href={item.link}
                              className="flex items-center space-x-3 group cursor-pointer"
                            >
                              <div className="flex-shrink-0">
                                <div
                                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                                  style={{ backgroundColor: `${itemColor}20` }}
                                >
                                  {icons[index]}
                                </div>
                              </div>
                              <span className={`${isPathActive(item.link) ? getActiveColor(item.link) : 'text-brand-dark-blue hover:text-blue-600'} font-semibold text-lg transition`}>
                                {item.title}
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* BusShield Image - Right Side */}
                    <div className="col-span-4 flex items-center justify-end">
                      <img
                        src={locale === 'de' ? BusShieldImage : BusShieldImageEn}
                        alt="BusShield"
                        className="h-auto w-full max-w-sm object-contain"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      
      {/* Mobile Menu Overlay */}
      <div className={`lg:hidden fixed inset-0 z-40 transition-all duration-300 ${
        mobileMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'
      }`}>
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Mobile Menu Panel */}
        <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
              <img src={BusAgentLogo} alt="BusAgent" className="h-12 w-auto" />
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>


            {/* Navigation Items */}
            <div className="flex-1 overflow-y-auto">
              <nav className="px-6 py-6 space-y-2">
                {Object.entries(navigationItems).map(([key, item]) => (
                  <div key={key} className="border-b border-gray-100 last:border-0">
                    <button
                      onClick={() => setMobileActiveDropdown(mobileActiveDropdown === key ? null : key)}
                      className="w-full flex items-center justify-between py-4 text-left"
                    >
                      <span className={`text-lg font-semibold ${isCategoryActive(key) ? getCategoryColor(key).replace('hover:text-blue-600', '').trim() : 'text-brand-dark-blue'}`}>{item.title}</span>
                      <svg
                        className={`h-5 w-5 text-gray-400 transform transition-transform ${
                          mobileActiveDropdown === key ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown Content */}
                    <div className={`overflow-hidden transition-all duration-300 ${
                      mobileActiveDropdown === key ? 'max-h-[600px] pb-4' : 'max-h-0'
                    }`}>
                      {item.sections && item.sections.map((section, sectionIdx) => (
                        <div key={sectionIdx}>
                          {section.heading && (
                            <div className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                              {section.heading}
                            </div>
                          )}
                          {(() => {
                            // Reorder items for "Unternehmen" section on mobile only
                            let itemsToRender = section.items;
                            if (key === 'unternehmen') {
                              // Find the items by link (language-independent)
                              const ueberUns = section.items.find(item => item.link === '/unternehmen/ueber-uns');
                              const career = section.items.find(item => item.link === '/unternehmen/career');
                              const kontakt = section.items.find(item => item.link === '/unternehmen/kontakt');
                              const veranstaltungen = section.items.find(item => item.link === '/unternehmen/veranstaltungen');

                              // Reorder: Über uns, Career, Kontakt, Veranstaltungen
                              itemsToRender = [ueberUns, career, kontakt, veranstaltungen].filter(Boolean);
                            }

                            return itemsToRender.map((subItem, idx) => (
                              <a
                                key={idx}
                                href={subItem.link}
                                className={`block py-3 pl-4 pr-2 ${isPathActive(subItem.link) ? `${getActiveColor(subItem.link)} ${getActiveColor(subItem.link).includes('orange') ? 'bg-orange-50' : 'bg-blue-50'}` : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'} rounded-lg transition`}
                              >
                                <div className={`font-medium flex items-center gap-2 ${isPathActive(subItem.link) ? getActiveColor(subItem.link) : ''}`}>
                                  <span>{subItem.title}</span>
                                  {subItem.beta && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-600">
                                      Beta
                                    </span>
                                  )}
                                </div>
                              </a>
                            ));
                          })()}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </div>

            {/* Mobile Menu Footer */}
            <div className="border-t border-gray-200 px-6 py-6 space-y-4">
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setMobileLanguageOpen(!mobileLanguageOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                >
                  <div className="flex items-center space-x-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium text-gray-700">{t('header.language')}</span>
                  </div>
                  <svg className={`h-5 w-5 text-gray-400 transform transition-transform ${mobileLanguageOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Language Dropdown */}
                <div className={`absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden transition-all duration-200 z-[100] ${
                  mobileLanguageOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                }`}>
                  <button
                    onClick={() => {
                      setLocale('de');
                      setMobileLanguageOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100"
                  >
                    {/* German Flag */}
                    <svg className="w-5 h-4" viewBox="0 0 20 14" fill="none">
                      <rect width="20" height="4.67" fill="#000000"/>
                      <rect y="4.67" width="20" height="4.67" fill="#DD0000"/>
                      <rect y="9.33" width="20" height="4.67" fill="#FFCE00"/>
                    </svg>
                    <span className="text-gray-700 font-medium">{t('header.deutsch')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setLocale('en');
                      setMobileLanguageOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    {/* UK Flag */}
                    <svg className="w-5 h-4" viewBox="0 0 20 14" fill="none">
                      <rect width="20" height="14" fill="#012169"/>
                      <path d="M0 0L20 14M20 0L0 14" stroke="white" strokeWidth="2.8"/>
                      <path d="M0 0L20 14M20 0L0 14" stroke="#C8102E" strokeWidth="1.4"/>
                      <path d="M10 0V14M0 7H20" stroke="white" strokeWidth="4.67"/>
                      <path d="M10 0V14M0 7H20" stroke="#C8102E" strokeWidth="2.8"/>
                    </svg>
                    <span className="text-gray-700 font-medium">{t('header.english')}</span>
                  </button>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => {
                  trackContactClick('header-mobile');
                  setIsContactModalOpen(true);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-center font-bold text-white bg-gradient-to-r from-blue-500 to-orange-400 rounded-full px-6 py-4 hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:from-blue-600 hover:to-orange-500"
              >
                {t('header.ctaButton')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </>
  );
};

export default Header;